// deviceRecords.js -- the phone's onboarding state (spec §5). Single job: read/write one validated
// JSON object under st_onboarding_v1 via platform storage, with an in-memory fallback, plus the per-account
// block record (#1464) under its own key, st_onboarding_blocks_v1, which only block writers touch: an
// ordinary write of the main record (another web tab's outbox or versions update) can never erase it, even
// in a read-modify-write race (Codex r3). Validation includes the record's retention: entries are clamped to
// now and dropped at 24h; there is no cap on active entries (#1469: evicting live evidence unblocks an account).
import { storageGet, storageSet } from '../../lib/platform';
import * as Sentry from '../../lib/errorReporting';
import { LOCK_MS } from './resolveOnboardingStep';

const KEY = 'st_onboarding_v1';
const BLOCKS_KEY = 'st_onboarding_blocks_v1';
export const EMPTY_DEVICE = Object.freeze({ welcomeSeen: false, age: null, lock: null, legal: null, accountPass: null, lastVersions: null, queue: [], blocks: {} });
let memory = null;
// This context's own block writes, kept in case storage refuses them (quota); merged with storage on read.
let blocksMemory = null;
const isIso = (s) => typeof s === 'string' && !Number.isNaN(Date.parse(s));
const isStr = (s) => typeof s === 'string' && s.length > 0;
const uidOrNull = (s) => (s === null || s === undefined ? null : (isStr(s) ? s : undefined));
const DATE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
// The DB version column is ^[a-z0-9.-]{1,32}$: 'age-v' plus at most 27 digits.
const AGE_VERSION = /^age-v[0-9]{1,27}$/;
const isDate = (s) => typeof s === 'string' && DATE.test(s);

// #1464: per-account under_21 evidence, { accountId: atIso }. Keys are account ids (never __proto__; the map
// has no prototype, readers use hasOwnProperty); values must parse. Entries 24h old or more are dropped. A
// future-dated `at` (the clock moved back after the write) is clamped to now, so skew cannot stretch a block
// past 24h (pre-Codex Sec F1). No cap: every active entry is kept until it expires (#1469, GPT #1465 F1).
function cleanBlocks(raw) {
  const blocks = Object.create(null);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return blocks;
  const now = Date.now();
  const kept = [];
  for (const k of Object.keys(raw)) {
    const at = raw[k];
    if (isStr(k) && k !== '__proto__' && isIso(at) && now - Math.min(Date.parse(at), now) < LOCK_MS) {
      kept.push([k, Date.parse(at) > now ? new Date(now).toISOString() : at]);
    }
  }
  for (const [k, at] of kept) blocks[k] = at;
  return blocks;
}
// Each account's later `at` wins.
function laterBlocks(a, b) {
  const out = { ...a };
  for (const k of Object.keys(b || {})) if (!out[k] || Date.parse(b[k]) > Date.parse(out[k])) out[k] = b[k];
  return out;
}
// Read fresh from storage every time (another tab may have written), merged with this context's own writes.
// A read never writes the key (Codex r4: a cleanup write-back is a read-modify-write that can erase a block
// another tab records between the read and the write). Reads ignore expired entries; storage drops them at the
// next block write. Accepted residual (Nicky 2026-10-12): an expired entry can stay in storage until then.
function readBlocks() {
  const raw = storageGet(BLOCKS_KEY);
  let stored = null;
  if (raw != null) {
    try {
      stored = JSON.parse(raw);
    } catch {
      // Recoverable: this context's own writes still count (CC-IMPL-037 warning).
      Sentry.captureMessage('onboarding: block record unreadable', { level: 'warning', tags: { function: 'readBlocks' } });
    }
  }
  return cleanBlocks(laterBlocks(cleanBlocks(stored), cleanBlocks(blocksMemory)));
}
// Add evidence: each account's later `at` wins; nothing is ever removed here except by retention.
function writeBlocks(add) {
  const next = cleanBlocks(laterBlocks(readBlocks(), cleanBlocks(add)));
  blocksMemory = next;
  storageSet(BLOCKS_KEY, JSON.stringify(next));
  return next;
}

function clean(raw) {
  const o = raw && typeof raw === 'object' ? raw : {};
  const ageAt = uidOrNull(o.age && o.age.attachedTo);
  const age = o.age && o.age.result === 'passed' && isStr(o.age.version) && isIso(o.age.attestedAt) && ageAt !== undefined
    ? { result: 'passed', version: o.age.version, attestedAt: o.age.attestedAt, attachedTo: ageAt } : null;
  const lock = o.lock && isIso(o.lock.at) ? { at: o.lock.at } : null;
  const legalAt = uidOrNull(o.legal && o.legal.attachedTo);
  const legal = o.legal && isStr(o.legal.terms) && isStr(o.legal.privacy) && isIso(o.legal.attestedAt) && legalAt !== undefined
    ? { terms: o.legal.terms, privacy: o.legal.privacy, attestedAt: o.legal.attestedAt, attachedTo: legalAt } : null;
  const ap = o.accountPass;
  const accountPass = ap && isStr(ap.userId) && isStr(ap.terms) && isStr(ap.privacy) ? { userId: ap.userId, terms: ap.terms, privacy: ap.privacy } : null;
  const lv = o.lastVersions;
  const lastVersions = lv && isDate(lv.terms) && isDate(lv.privacy) && typeof lv.age === 'string' && AGE_VERSION.test(lv.age)
    ? { terms: lv.terms, privacy: lv.privacy, age: lv.age } : null;
  const queue = Array.isArray(o.queue)
    ? o.queue.filter((q) => q && isStr(q.id) && isStr(q.userId) && Array.isArray(q.rows) && isIso(q.queuedAt))
      .map((q) => ({ id: q.id, userId: q.userId, rows: q.rows, queuedAt: q.queuedAt, parked: q.parked === true, alerted: q.alerted === true }))
    : [];
  return { welcomeSeen: o.welcomeSeen === true, age, lock, legal, accountPass, lastVersions, queue };
}

// Once this session has written, memory is the truth: storage that reads but refuses writes (quota) would
// otherwise hand back a stale copy and silently drop queued consent rows or a lock.
function readStored() {
  const raw = storageGet(KEY);
  if (raw == null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    // Recoverable: this context's own record is written as is (CC-IMPL-037 warning).
    Sentry.captureMessage('onboarding: stored device record unreadable during merge', { level: 'warning', tags: { function: 'readStored' } });
    return undefined;
  }
}
function readMain() {
  if (memory) return clean(memory);
  const raw = storageGet(KEY);
  if (raw == null) return clean({});
  try {
    return clean(JSON.parse(raw));
  } catch {
    // Recoverable: the user is asked again (CC-IMPL-037 warning).
    Sentry.captureMessage('onboarding: device record unreadable', { level: 'warning', tags: { function: 'readDevice' } });
    return clean({});
  }
}
export function readDevice() {
  return { ...readMain(), blocks: readBlocks() };
}
// The phone lock written by another JS context (a second web tab) since this one last read is merged in, not
// overwritten (pre-Codex FC F2): the later lock wins unless it is future-dated (6(d) rewrites those to now,
// so a stored future lock is not reinstated). A lock lost to a cross-tab race is re-stamped from the block
// record at the account's next blocked render (6(b)).
function mergeStoredLock(next) {
  const stored = readStored();
  if (!stored || typeof stored !== 'object') return next;
  const s = clean(stored);
  const now = Date.now();
  const lock = s.lock && Date.parse(s.lock.at) <= now && (!next.lock || Date.parse(s.lock.at) > Date.parse(next.lock.at)) ? s.lock : next.lock;
  return { ...next, lock };
}
export function writeDevice(patch) {
  const { blocks: addBlocks, ...rest } = patch || {};
  const blocks = addBlocks !== undefined ? writeBlocks(addBlocks) : readBlocks();
  const next = mergeStoredLock(clean({ ...readMain(), ...rest }));
  memory = next;
  storageSet(KEY, JSON.stringify(next));
  return { ...next, blocks };
}
export function updateDevice(fn) {
  return writeDevice(fn(readDevice()));
}
// Another JS context (web tab) wrote: merge its lock into this context's memory (the block record is read
// fresh from storage every time), which would otherwise keep returning the stale copy until this context
// writes (#1464 Codex r1 P1). Returns the merged record.
export function syncFromStorage() {
  if (memory) memory = mergeStoredLock(clean(memory));
  return readDevice();
}
export const DEVICE_KEY = KEY;
export const DEVICE_BLOCKS_KEY = BLOCKS_KEY;
export function resetDeviceMemoryForTests() { memory = null; blocksMemory = null; }
